import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import useService from '../../hooks/service.hook';
import AuthenticationService from '../../services/authentication.service';
import { isEmailInvalid } from '../../helpers/general.helpers';
import { User } from '../../models/user.models';
import GuardianService from '../../services/guardian.service';
import { palette } from '../../theme';
import SplitPanelLayout from './components/SplitPanelLayout';
import DobStep from './components/DobStep';
import RoleSelectionStep, { OnboardingRole } from './components/RoleSelectionStep';
import GuardianWelcomeStep from './components/GuardianWelcomeStep';
import AthleteSignUpForm from './components/AthleteSignUpForm';
import { FieldErrors, useFieldErrors } from '../../components/form-validation';

const ATHLETE_GRADIENT = 'linear-gradient(135deg, #1565E8 0%, #3D8EFF 100%)';
const GUARDIAN_GRADIENT = 'linear-gradient(135deg, #C95B0A 0%, #F2994A 100%)';
const GUARDIAN_ORANGE = '#E06A10';

type Screen = 'dob' | 'role' | 'guardian-welcome' | 'form' | 'invite-guardian';

// ─── Age helper ──────────────────────────────────────────────────────────────

function computeIsUnder18(birthday: string): boolean {
  if (!birthday) return false;
  const today = new Date();
  const birth = new Date(birthday);
  const age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    return age - 1 < 18;
  }
  return age < 18;
}

// ─── Component ───────────────────────────────────────────────────────────────

const CreateAccountPage: FC = () => {
  const [screen, setScreen] = useState<Screen>('dob');
  const [role, setRole] = useState<OnboardingRole | null>(null);

  // Form fields — birthday collected in DobStep, carried through the whole flow
  const [birthday, setBirthday] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sex, setSex] = useState('');
  type AccountField =
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'password'
    | 'confirmPassword'
    | 'birthday'
    | 'sex'
    | 'acceptedTerms';

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { fieldErrors, setFieldErrors, resetFieldErrors } = useFieldErrors<AccountField>();

  // Post-signup guardian invite
  const [guardianEmail, setGuardianEmail] = useState('');
  const [guardianError, setGuardianError] = useState('');
  const [authUserCreds, setAuthUserCreds] = useState<any>(null);

  const navigate = useNavigate();
  const { showSnackbar } = useSnackbarHelper();
  const authenticationService = useService(AuthenticationService);
  const guardianService = useService(GuardianService);

  // ─── DOB step ──────────────────────────────────────────────────────────────

  const handleDobContinue = () => {
    if (computeIsUnder18(birthday)) {
      // Under 18 → skip role selection, go straight to athlete form
      setRole('athlete');
      setScreen('form');
    } else {
      setScreen('role');
    }
  };

  // ─── Role step ─────────────────────────────────────────────────────────────

  const handleRoleNext = () => {
    if (!role) return;
    if (role === 'guardian') {
      setScreen('guardian-welcome');
    } else {
      setScreen('form');
    }
  };

  // ─── Validation ────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const errors: FieldErrors<AccountField> = {};
    if (!firstName) errors.firstName = 'First name is required';
    if (!lastName) errors.lastName = 'Last name is required';
    if (!email) {
      errors.email = 'Email is required';
    } else if (isEmailInvalid(email)) {
      errors.email = 'Email is invalid';
    }
    if (!password) {
      errors.password = 'Password must be at least 8 characters';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (!birthday) errors.birthday = 'Date of birth is required';
    if (!sex) errors.sex = 'Sex is required';
    if (!acceptedTerms) errors.acceptedTerms = 'You must accept the Terms and Conditions';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateGuardianEmail = (): boolean => {
    if (!guardianEmail) {
      setGuardianError('No guardian email was entered');
      return false;
    }
    if (isEmailInvalid(guardianEmail)) {
      setGuardianError('Guardian email is invalid');
      return false;
    }
    return true;
  };

  // ─── Auth handlers ─────────────────────────────────────────────────────────

  const handleLogin = (creds: any) => {
    setIsLoading(true);
    authenticationService
      .loginUser(creds)
      .then((response: any) => {
        const d = response.data.data;
        const userData: User = {
          token: d.token,
          firstName: d.firstName,
          lastName: d.lastName,
          email: d.email,
          id: d.userId,
          role: d.role,
          expiration: d.exp,
          profileImage: d.profileImage,
          measurementSystem: d.measurementSystem,
          birthday: d.birthday,
          sex: d.sex,
        };
        window.localStorage.setItem('user', JSON.stringify(userData));
        window.localStorage.setItem('coelab_onboarding_pending', 'true');
        window.localStorage.setItem('coelab_onboarding_role', role ?? 'athlete');
        navigate('/home');
      })
      .catch(() => {
        setIsLoading(false);
        showSnackbar('Error logging in. Please try again later.', 'error');
      });
  };

  const handleCreateAccount = () => {
    resetFieldErrors();
    if (!validate()) return;

    setIsLoading(true);
    const payload = { email, password, firstname: firstName, lastname: lastName, birthday, sex };

    authenticationService
      .signupUser(payload)
      .then((response: any) => {
        const creds = response.data.data;
        setAuthUserCreds(creds);

        if (computeIsUnder18(birthday)) {
          setIsLoading(false);
          setScreen('invite-guardian');
        } else {
          handleLogin(creds);
        }
      })
      .catch((err: any) => {
        setIsLoading(false);
        const msg =
          err?.request?.status === 202
            ? 'This user already exists. Please log in.'
            : 'There was an error signing up. Please try again later.';
        showSnackbar(msg, 'error');
      });
  };

  const handleInviteGuardian = () => {
    setGuardianError('');
    if (!validateGuardianEmail()) return;

    setIsLoading(true);
    authenticationService
      .loginUser(authUserCreds)
      .then((response: any) => {
        const d = response.data.data;
        const userData: User = {
          token: d.token,
          firstName: d.firstName,
          lastName: d.lastName,
          email: d.email,
          id: d.userId,
          role: d.role,
          expiration: d.exp,
          profileImage: d.profileImage,
          measurementSystem: d.measurementSystem,
          birthday: d.birthday,
          sex: d.sex,
        };
        window.localStorage.setItem('user', JSON.stringify(userData));

        guardianService
          .inviteGuardian(guardianEmail)
          .then(() => {
            showSnackbar(
              'Successfully invited your guardian to CoeLab. They must accept their invitation in order for you to join a team.',
              'success'
            );
            window.localStorage.setItem('coelab_onboarding_pending', 'true');
            window.localStorage.setItem('coelab_onboarding_role', role ?? 'athlete');
            navigate('/home');
          })
          .catch(() => {
            showSnackbar('There was an error inviting this guardian. Please go to Settings to try again.', 'error');
            window.localStorage.setItem('coelab_onboarding_pending', 'true');
            window.localStorage.setItem('coelab_onboarding_role', role ?? 'athlete');
            navigate('/home');
          });
      })
      .catch(() => {
        setIsLoading(false);
        showSnackbar('Error logging in. Please try again later.', 'error');
      });
  };

  // ─── Panel config per screen ───────────────────────────────────────────────

  const isGuardianFlow = role === 'guardian';
  const gradient = isGuardianFlow ? GUARDIAN_GRADIENT : ATHLETE_GRADIENT;
  const accentColor = isGuardianFlow ? GUARDIAN_ORANGE : palette.primary.main;

  // ─── Screens ───────────────────────────────────────────────────────────────

  if (screen === 'dob') {
    return (
      <SplitPanelLayout
        gradient={ATHLETE_GRADIENT}
        headline={'Train smarter.\nPerform better.'}
        tagline="The complete platform for athletes, coaches, and guardians to connect, train, and grow together."
      >
        <DobStep birthday={birthday} onBirthdayChange={setBirthday} onContinue={handleDobContinue} />
      </SplitPanelLayout>
    );
  }

  if (screen === 'role') {
    return (
      <SplitPanelLayout
        gradient={ATHLETE_GRADIENT}
        headline="Who are you?"
        tagline="Select your role to get a personalized experience tailored to your needs."
        step={2}
        totalSteps={8}
      >
        <RoleSelectionStep
          selectedRole={role}
          onRoleChange={setRole}
          onBack={() => setScreen('dob')}
          onNext={handleRoleNext}
        />
      </SplitPanelLayout>
    );
  }

  if (screen === 'guardian-welcome') {
    return (
      <SplitPanelLayout
        gradient={GUARDIAN_GRADIENT}
        headline="Guardian Access"
        tagline="Stay connected to your athlete's journey with full visibility into training and team communication."
        step={3}
        totalSteps={8}
      >
        <GuardianWelcomeStep onContinue={() => setScreen('form')} onBack={() => setScreen('role')} />
      </SplitPanelLayout>
    );
  }

  if (screen === 'invite-guardian') {
    return (
      <SplitPanelLayout
        gradient={ATHLETE_GRADIENT}
        headline={'Train smarter.\nPerform better.'}
        tagline="The complete platform for athletes, coaches, and guardians to connect, train, and grow together."
      >
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>
            Account Created!
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3} sx={{ maxWidth: 380 }}>
            Since you are younger than 18 you must have at least one guardian attached to your account to join a team.
            You can invite them below or later in Settings.
          </Typography>

          {guardianError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {guardianError}
            </Alert>
          )}

          <TextField
            label="Guardian Email"
            placeholder="guardian@example.com"
            value={guardianEmail}
            onChange={(e) => {
              setGuardianEmail(e.target.value);
              setGuardianError('');
            }}
            error={!!guardianError}
            helperText={guardianError || ''}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            fullWidth
            disabled={isLoading}
            onClick={handleInviteGuardian}
            sx={{ borderRadius: 999, py: 1.25, fontWeight: 600, mb: 1 }}
          >
            Invite Guardian
          </Button>
          <Button
            variant="text"
            fullWidth
            disabled={isLoading}
            onClick={() => handleLogin(authUserCreds)}
            sx={{ color: palette.text.secondary, fontWeight: 500 }}
          >
            Skip for now
          </Button>
        </Box>
      </SplitPanelLayout>
    );
  }

  // screen === 'form'
  return (
    <SplitPanelLayout
      gradient={gradient}
      headline={isGuardianFlow ? 'Guardian Access' : 'Train smarter.\nPerform better.'}
      tagline={
        isGuardianFlow
          ? "Stay connected to your athlete's journey with full visibility into training and team communication."
          : 'The complete platform for athletes, coaches, and guardians to connect, train, and grow together.'
      }
    >
      <AthleteSignUpForm
        firstName={firstName}
        lastName={lastName}
        email={email}
        password={password}
        confirmPassword={confirmPassword}
        birthday={birthday}
        sex={sex}
        acceptedTerms={acceptedTerms}
        fieldErrors={fieldErrors}
        isLoading={isLoading}
        accentColor={accentColor}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onConfirmPasswordChange={setConfirmPassword}
        onBirthdayChange={setBirthday}
        onSexChange={setSex}
        onAcceptedTermsChange={setAcceptedTerms}
        onSubmit={handleCreateAccount}
        onBack={() => {
          resetFieldErrors();
          if (isGuardianFlow) {
            setScreen('guardian-welcome');
          } else if (computeIsUnder18(birthday)) {
            setScreen('dob');
          } else {
            setScreen('role');
          }
        }}
      />
    </SplitPanelLayout>
  );
};

export default CreateAccountPage;
